import { ContextResponse, InputEstimatedCost, TotalCost } from '../common/types/solution';
import { API_BASE, API_GRAPH_BASE, API_LLM_BASE, STORAGE_BASE } from '../config/env';
import { httpConstants } from '../constants';
import { httpService } from '../utility/httpService';

export const getEstimatedCost = (payload: InputEstimatedCost): Promise<TotalCost> => {
  return httpService(
    httpConstants.METHOD_TYPE.POST,
    `${API_LLM_BASE}/api/ai/v2/ideation-studio/generate-total-cost/`,
    payload
  );
};

export const generatedOpportunities = (payload: ContextResponse): Promise<ContextResponse> => {
  return httpService(
    httpConstants.METHOD_TYPE.POST,
    `${API_LLM_BASE}/api/ai/opportunity/generation/`,
    payload
  );
};
export interface CreateUseCasePayload {
  use_case?: string;
  expected_impact?: string;
  current_process?: string;
  domain: string;
  function: string;
  function_id: string | null;
  process_group: string;
  process_group_id: string | null;
  process: string;
  process_id: string | null;
  hackette_score?: string;
  user_score?: string;
  status?: string;
  impact?: string;
  organization_id: string;
  created_at: number;
  updated_at: number;
  recent_imported_chat?: Record<string, any>;
}

export interface CreateUseCaseResponse {
  id: string;
  use_case: string;
  expected_impact: string;
  impact: string;
  organization_id: string | null;
  domain: string;
  function: string;
  function_id: string | null;
  process_group: string;
  process_group_id: string | null;
  process: string;
  process_id: string | null;
  status: string;
  hackette_score: string;
  ice_score: Record<string, any>;
  bulk_upload_id: string | null;
  title: string;
  created_at: number;
  updated_at: number;
}

export interface TechLandscapePayload {
  organization_id: string;
  diagram_xml: string;
  step_descriptions: any;
  ai_opportunities: any[];
}

export interface TechLandscapeResponse {
  xml: string;
  step_descriptions: any;
  ai_opportunities: any[];
}
export interface CustomFunctionResponse {
  functions?: any[];
}
export interface CustomProcessPayload {
  function_id: string;
}

export interface CustomProcessResponse {
  processes?: any[];
}
export interface ProcessGroupResponse {
  process_groups?: any[];
}
export const fetchTechLandscapeDiagram = (
  payload: TechLandscapePayload
): Promise<TechLandscapeResponse> => {
  return httpService(
    httpConstants.METHOD_TYPE.POST,
    `${API_LLM_BASE}/api/ai/v1/diagram/edit-diagram-tech-landscape/`,
    payload
  );
};
export interface BpmnFileDetailsResponse {
  id: string;
  opportunity_report_id: string;
  bpmn_type: 'to_be_process' | 'current_process' | 'agentic_process' | string;
  bpmn_xml_content: string;
  file_url: string;
  file_name: string;
  file_size: number;
  description: string;
  created_at: string;
}

export interface BpmnFileDetailsPayload {
  bpmn_type: 'to_be_process' | 'current_process' | 'agentic_process' | string;
  bpmn_xml_content: string;
  file_url: string;
  file_name: string;
  file_size: number;
  description: string;
  opportunity_report_id: string;
}
export interface DataSourcesResponse {
  data_sources: {
    external: {
      [key: string]: {
        structured: string[];
        unstructured: string[];
      };
    };
    internal: {
      [key: string]: {
        structured: string[];
        unstructured: string[];
      };
    };
  };
  solution_id: string;
  organization_id: string;
  diagram_id: string;
  agent_available: boolean;
  opportunity_id: string;
}

export const fetchBpmnXmlForProcess = async (
  oppID: string,
  solutionId: string,
  bpmn_file_id: string,
  payload: BpmnFileDetailsPayload
): Promise<BpmnFileDetailsResponse> => {
  const blob = new Blob([payload.bpmn_xml_content], { type: 'text/xml' });
  const bpmnFile = new File([blob], `${payload.bpmn_type}.bpmn`, { type: 'text/xml' });
  const formData = new FormData();
  formData.append('upload', bpmnFile, `${payload.bpmn_type}.bpmn`);

  const res = await httpService<{ files: string[]; upload: { id: string } }>(
    httpConstants.METHOD_TYPE.POST,
    `${API_BASE}/api/nexus/files`,
    formData
  );
  payload.file_url = STORAGE_BASE + res.files[0];
  payload.file_size = bpmnFile.size;
  payload.opportunity_report_id = oppID;

  const url = `${API_GRAPH_BASE}/new-solution/${solutionId}/bpmn-files/${bpmn_file_id}`;

  return httpService(httpConstants.METHOD_TYPE.PUT, url, payload);
};
export interface BpmnFileContentResponse extends BpmnFileDetailsResponse {}

export const getBpmnFileContent = async (
  solutionId: string,
  fileID: string
): Promise<BpmnFileContentResponse> => {
  const url = `${API_GRAPH_BASE}/new-solution/${solutionId}/bpmn-files/${fileID}`;

  return httpService(httpConstants.METHOD_TYPE.GET, url);
};
export interface DataSourcesResponse extends BpmnFileDetailsResponse {}
export const getDataSources = async (
  diagram_id: string,
  organization_id: string
): Promise<DataSourcesResponse> => {
  const url = `${API_BASE}/api/v1/e2e_taxonomy/data-sources/?diagram_id=${diagram_id}&organization_id=${organization_id}`;

  return httpService(httpConstants.METHOD_TYPE.GET, url);
};
export const getCustomProcesses = async (
  processGroupId: string
): Promise<CustomProcessResponse> => {
  const url = `${API_GRAPH_BASE}/custom-process/processes?function_id=${processGroupId}`;
  return httpService(httpConstants.METHOD_TYPE.GET, url);
};
export const getCustomFunctions = async (
  organizationId: string,
  office?: number
): Promise<CustomFunctionResponse> => {
  const url = `${API_GRAPH_BASE}/custom-process/functions?organization_id=${organizationId}${office ? `&office=${office}` : ''}`;
  return httpService(httpConstants.METHOD_TYPE.GET, url);
};
export const getProcessGroups = async (
  organizationId: string,
  categoryId: string
): Promise<ProcessGroupResponse> => {
  const url = `${API_GRAPH_BASE}/custom-process/process-groups?organization_id=${organizationId}&category_id=${categoryId}`;
  return httpService(httpConstants.METHOD_TYPE.GET, url);
};
export const createUseCase = async (
  payload: CreateUseCasePayload
): Promise<CreateUseCaseResponse> => {
  const url = `${API_BASE}/api/v1/ideation/use-cases/`;

  return httpService(httpConstants.METHOD_TYPE.POST, url, payload);
};
